/**
 * useUserStatistics Hook
 *
 * Custom hook for computing user climbing statistics from climb logs.
 * Supports filtering by gym and date range, with computed metrics like
 * grade pyramid, activity over time, and climbing streaks.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  getDoc,
  doc,
  type DocumentData,
  type Unsubscribe,
} from 'firebase/firestore';
import { getFirebaseDb } from '@/lib/firebase';
import { useAuth } from '@/hooks/useAuth';
import { V_GRADES, type VGrade, type ClimbLog, type ClimbOutcome } from '@flash/shared';

/**
 * Filter options for statistics
 */
export interface StatisticsFilters {
  /** Filter by gym ID (null = all gyms) */
  gymId?: string | null | undefined;
  /** Start date for filtering (inclusive) */
  startDate?: Date | null | undefined;
  /** End date for filtering (inclusive) */
  endDate?: Date | null | undefined;
}

/**
 * Sends by grade for pyramid visualization
 */
export interface GradeCount {
  /** The V-grade */
  grade: VGrade;
  /** Number of sends at this grade */
  sendCount: number;
  /** Number of attempts at this grade */
  attemptCount: number;
}

/**
 * Activity data point for time-series chart
 */
export interface ActivityDataPoint {
  /** Date string in YYYY-MM-DD format */
  date: string;
  /** Number of sends on this date */
  sends: number;
  /** Number of attempts on this date */
  attempts: number;
}

/**
 * Extended climb log with route grade for statistics
 */
interface ClimbLogWithGrade extends ClimbLog {
  routeGrade?: VGrade | undefined;
}

/**
 * Computed user statistics
 */
export interface UserStatistics {
  /** Total number of sends */
  totalSends: number;
  /** Total number of attempts */
  totalAttempts: number;
  /** Total climb logs */
  totalLogs: number;
  /** Number of unique routes climbed */
  uniqueRoutes: number;
  /** Sends grouped by grade for pyramid */
  sendsByGrade: GradeCount[];
  /** Activity data points for chart */
  activityByDate: ActivityDataPoint[];
  /** Current climbing streak (consecutive days with at least one climb) */
  currentStreak: number;
  /** Longest climbing streak */
  longestStreak: number;
  /** Most frequently climbed grade */
  mostFrequentGrade: VGrade | null;
  /** Highest grade sent */
  highestGrade: VGrade | null;
}

/**
 * State interface for user statistics
 */
interface UseUserStatisticsState {
  /** Computed statistics */
  statistics: UserStatistics | null;
  /** Whether data is being loaded */
  loading: boolean;
  /** Error message if query failed */
  error: string | null;
}

/**
 * Return type for useUserStatistics hook
 */
interface UseUserStatisticsResult extends UseUserStatisticsState {
  /** Current filters applied */
  filters: StatisticsFilters;
  /** Update filters */
  setFilters: (filters: StatisticsFilters) => void;
  /** Clear all filters */
  clearFilters: () => void;
  /** Raw climb logs for additional processing */
  logs: ClimbLog[];
}

/**
 * Default filters
 */
const DEFAULT_FILTERS: StatisticsFilters = {
  gymId: null,
  startDate: null,
  endDate: null,
};

/**
 * Default statistics
 */
const DEFAULT_STATISTICS: UserStatistics = {
  totalSends: 0,
  totalAttempts: 0,
  totalLogs: 0,
  uniqueRoutes: 0,
  sendsByGrade: V_GRADES.map((grade) => ({ grade, sendCount: 0, attemptCount: 0 })),
  activityByDate: [],
  currentStreak: 0,
  longestStreak: 0,
  mostFrequentGrade: null,
  highestGrade: null,
};

/**
 * Convert Firestore document to ClimbLog type
 */
const docToClimbLog = (docData: DocumentData, docId: string): ClimbLogWithGrade => {
  return {
    id: docId,
    userId: docData['userId'] as string,
    routeId: docData['routeId'] as string,
    gymId: docData['gymId'] as string,
    outcome: docData['outcome'] as ClimbOutcome,
    notes: (docData['notes'] as string | null) ?? null,
    loggedAt: docData['loggedAt'],
    createdAt: docData['createdAt'],
    routeGrade: docData['routeGrade'] as VGrade | undefined,
  };
};

/**
 * Get timestamp in milliseconds from a log's loggedAt field
 */
const getLogTimestamp = (loggedAt: unknown): number => {
  if (!loggedAt) return 0;

  // Handle Firestore Timestamp
  if (typeof loggedAt === 'object' && loggedAt !== null && 'toMillis' in loggedAt) {
    return (loggedAt as { toMillis: () => number }).toMillis();
  }

  // Handle Date object
  if (loggedAt instanceof Date) {
    return loggedAt.getTime();
  }

  // Handle timestamp with seconds/nanoseconds
  if (
    typeof loggedAt === 'object' &&
    loggedAt !== null &&
    'seconds' in loggedAt &&
    'nanoseconds' in loggedAt
  ) {
    const ts = loggedAt as { seconds: number; nanoseconds: number };
    return ts.seconds * 1000 + ts.nanoseconds / 1000000;
  }

  return 0;
};

/**
 * Format date to YYYY-MM-DD string
 */
const formatDate = (timestamp: number): string => {
  const date = new Date(timestamp);
  const isoString = date.toISOString().split('T')[0];
  return isoString ?? '';
};

/**
 * Calculate current and longest climbing streaks
 */
const calculateStreaks = (dates: string[]): { current: number; longest: number } => {
  if (dates.length === 0) {
    return { current: 0, longest: 0 };
  }

  // Sort dates in descending order (most recent first)
  const sortedDates = [...new Set(dates)].sort().reverse();

  // Check if the most recent date is today or yesterday for current streak
  const today = formatDate(Date.now());
  const yesterday = formatDate(Date.now() - 24 * 60 * 60 * 1000);

  let currentStreak = 0;
  let longestStreak = 0;
  let tempStreak = 0;

  // Calculate longest streak
  const sortedAsc = [...sortedDates].reverse();
  for (let i = 0; i < sortedAsc.length; i++) {
    const currentDateStr = sortedAsc[i];
    if (i === 0) {
      tempStreak = 1;
    } else {
      const prevDateStr = sortedAsc[i - 1];
      if (!prevDateStr || !currentDateStr) continue;
      const prevDate = new Date(prevDateStr);
      const currDate = new Date(currentDateStr);
      const diffDays = Math.round(
        (currDate.getTime() - prevDate.getTime()) / (24 * 60 * 60 * 1000)
      );

      if (diffDays === 1) {
        tempStreak++;
      } else {
        longestStreak = Math.max(longestStreak, tempStreak);
        tempStreak = 1;
      }
    }
  }
  longestStreak = Math.max(longestStreak, tempStreak);

  // Calculate current streak (must include today or yesterday)
  const mostRecentDate = sortedDates[0];
  if (mostRecentDate && (mostRecentDate === today || mostRecentDate === yesterday)) {
    currentStreak = 1;
    for (let i = 1; i < sortedDates.length; i++) {
      const prevDateStr = sortedDates[i - 1];
      const currDateStr = sortedDates[i];
      if (!prevDateStr || !currDateStr) break;
      const prevDate = new Date(prevDateStr);
      const currDate = new Date(currDateStr);
      const diffDays = Math.round(
        (prevDate.getTime() - currDate.getTime()) / (24 * 60 * 60 * 1000)
      );

      if (diffDays === 1) {
        currentStreak++;
      } else {
        break;
      }
    }
  }

  return { current: currentStreak, longest: longestStreak };
};

/**
 * Hook to compute user climbing statistics
 *
 * @param options - Optional initial filters
 * @returns Object containing statistics, loading state, error, and filter functions
 *
 * @example
 * ```tsx
 * const {
 *   statistics,
 *   loading,
 *   error,
 *   filters,
 *   setFilters,
 * } = useUserStatistics({
 *   initialFilters: { gymId: 'gym-123' },
 * });
 *
 * if (loading) return <LoadingSpinner />;
 * if (error) return <ErrorMessage message={error} />;
 *
 * return <StatisticsDisplay statistics={statistics} />;
 * ```
 */
export const useUserStatistics = (
  options: {
    initialFilters?: StatisticsFilters;
  } = {}
): UseUserStatisticsResult => {
  const { initialFilters = DEFAULT_FILTERS } = options;
  const { user } = useAuth();

  const [logs, setLogs] = useState<ClimbLogWithGrade[]>([]);
  const [state, setState] = useState<UseUserStatisticsState>({
    statistics: null,
    loading: true,
    error: null,
  });
  const [filters, setFilters] = useState<StatisticsFilters>(initialFilters);

  /**
   * Clear all filters to defaults
   */
  const clearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, []);

  // Real-time subscription effect for climb logs
  useEffect(() => {
    if (!user) {
      setLogs([]);
      setState({ statistics: DEFAULT_STATISTICS, loading: false, error: null });
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    const db = getFirebaseDb();
    const logsRef = collection(db, 'climbLogs');

    // Build query with userId filter and sort by loggedAt (newest first)
    let logsQuery = query(logsRef, where('userId', '==', user.uid), orderBy('loggedAt', 'desc'));

    // Add gymId filter if specified
    if (filters.gymId) {
      logsQuery = query(
        logsRef,
        where('userId', '==', user.uid),
        where('gymId', '==', filters.gymId),
        orderBy('loggedAt', 'desc')
      );
    }

    // Set up real-time listener
    const unsubscribe: Unsubscribe = onSnapshot(
      logsQuery,
      async (snapshot) => {
        let fetchedLogs: ClimbLogWithGrade[] = snapshot.docs.map((docSnapshot) =>
          docToClimbLog(docSnapshot.data(), docSnapshot.id)
        );

        // Apply client-side date filtering if specified
        if (filters.startDate) {
          const startTime = filters.startDate.getTime();
          fetchedLogs = fetchedLogs.filter((log) => {
            const logTime = getLogTimestamp(log.loggedAt);
            return logTime >= startTime;
          });
        }

        if (filters.endDate) {
          // Add one day to include the end date fully
          const endTime = filters.endDate.getTime() + 24 * 60 * 60 * 1000;
          fetchedLogs = fetchedLogs.filter((log) => {
            const logTime = getLogTimestamp(log.loggedAt);
            return logTime < endTime;
          });
        }

        // Fetch route grades for each unique routeId
        const uniqueRouteIds = [...new Set(fetchedLogs.map((log) => log.routeId))];
        const routeGradeMap = new Map<string, VGrade>();

        await Promise.all(
          uniqueRouteIds.map(async (routeId) => {
            try {
              const routeDoc = await getDoc(doc(db, 'routes', routeId));
              if (routeDoc.exists()) {
                const data = routeDoc.data();
                const grade = data['officialGrade'] as VGrade | undefined;
                if (grade) {
                  routeGradeMap.set(routeId, grade);
                }
              }
            } catch {
              // Route not found or error fetching - leave grade undefined
            }
          })
        );

        // Merge route grades into logs
        const logsWithGrades = fetchedLogs.map((log) => ({
          ...log,
          routeGrade: routeGradeMap.get(log.routeId),
        }));

        setLogs(logsWithGrades);
        setState((prev) => ({ ...prev, loading: false }));
      },
      (err) => {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch climb logs';
        console.error('Error in statistics subscription:', err);
        setState((prev) => ({ ...prev, loading: false, error: errorMessage }));
      }
    );

    return () => {
      unsubscribe();
    };
  }, [user, filters.gymId, filters.startDate, filters.endDate]);

  // Compute statistics from logs
  const statistics = useMemo((): UserStatistics => {
    if (logs.length === 0) {
      return DEFAULT_STATISTICS;
    }

    // Basic counts
    const totalSends = logs.filter((log) => log.outcome === 'send').length;
    const totalAttempts = logs.filter((log) => log.outcome === 'attempt').length;
    const totalLogs = logs.length;
    const uniqueRoutes = new Set(logs.map((log) => log.routeId)).size;

    // Sends by grade (for pyramid visualization)
    // Note: Since we may not have routeGrade in all logs, we'll track what we can
    const gradeCountMap = new Map<VGrade, { sends: number; attempts: number }>();
    V_GRADES.forEach((grade) => {
      gradeCountMap.set(grade, { sends: 0, attempts: 0 });
    });

    // Track grades from logs that have routeGrade
    logs.forEach((log) => {
      if (log.routeGrade) {
        const current = gradeCountMap.get(log.routeGrade);
        if (current) {
          if (log.outcome === 'send') {
            current.sends++;
          } else {
            current.attempts++;
          }
        }
      }
    });

    const sendsByGrade: GradeCount[] = V_GRADES.map((grade) => {
      const counts = gradeCountMap.get(grade);
      return {
        grade,
        sendCount: counts?.sends ?? 0,
        attemptCount: counts?.attempts ?? 0,
      };
    });

    // Activity by date (for time-series chart)
    const dateActivityMap = new Map<string, { sends: number; attempts: number }>();
    logs.forEach((log) => {
      const timestamp = getLogTimestamp(log.loggedAt);
      if (timestamp > 0) {
        const dateStr = formatDate(timestamp);
        const current = dateActivityMap.get(dateStr) ?? { sends: 0, attempts: 0 };
        if (log.outcome === 'send') {
          current.sends++;
        } else {
          current.attempts++;
        }
        dateActivityMap.set(dateStr, current);
      }
    });

    const activityByDate: ActivityDataPoint[] = Array.from(dateActivityMap.entries())
      .map(([date, activity]) => ({
        date,
        sends: activity.sends,
        attempts: activity.attempts,
      }))
      .sort((a, b) => a.date.localeCompare(b.date));

    // Calculate streaks
    const climbDates = logs
      .map((log) => {
        const timestamp = getLogTimestamp(log.loggedAt);
        return timestamp > 0 ? formatDate(timestamp) : '';
      })
      .filter(Boolean);
    const { current: currentStreak, longest: longestStreak } = calculateStreaks(climbDates);

    // Most frequent grade (among sends)
    let mostFrequentGrade: VGrade | null = null;
    let maxSendCount = 0;
    sendsByGrade.forEach(({ grade, sendCount }) => {
      if (sendCount > maxSendCount) {
        maxSendCount = sendCount;
        mostFrequentGrade = grade;
      }
    });

    // Highest grade sent
    let highestGrade: VGrade | null = null;
    for (let i = V_GRADES.length - 1; i >= 0; i--) {
      const grade = V_GRADES[i];
      if (!grade) continue;
      const gradeData = sendsByGrade.find((g) => g.grade === grade);
      if (gradeData && gradeData.sendCount > 0) {
        highestGrade = grade;
        break;
      }
    }

    return {
      totalSends,
      totalAttempts,
      totalLogs,
      uniqueRoutes,
      sendsByGrade,
      activityByDate,
      currentStreak,
      longestStreak,
      mostFrequentGrade,
      highestGrade,
    };
  }, [logs]);

  // Update state with computed statistics
  useEffect(() => {
    setState((prev) => ({
      ...prev,
      statistics,
    }));
  }, [statistics]);

  return {
    ...state,
    statistics,
    filters,
    setFilters,
    clearFilters,
    logs,
  };
};

export default useUserStatistics;
